// components/reactions/ReactionPickerButton.jsx
import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { BsEmojiSmileUpsideDown } from "react-icons/bs";
import EmojiPicker from "../emoji/emojiPicker";
import { useMessageReactions, setMyReactionOnDoc } from "./reactionHelpers";

export default function ReactionPickerButton({ docRef, currentUser }) {
  const { myRx } = useMessageReactions(docRef, currentUser);

  const anchorRef = useRef(null);       // the 😵 button
  const pickerBoxRef = useRef(null);    // the floating picker bubble
  const justOpenedRef = useRef(false);  // used to ignore the same click that opened it

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerVisible, setPickerVisible] = useState(false); // controls CSS visibility
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  // --- utility: wait until anchor stops moving for N frames ---
  // exactly like your older working version
  const waitForStableRect = useCallback((el, frames = 3, tol = 0.5) => {
    return new Promise((resolve) => {
      let last = null;
      let stable = 0;

      const step = () => {
        const r = el.getBoundingClientRect();
        if (
          last &&
          Math.abs(r.top - last.top) < tol &&
          Math.abs(r.left - last.left) < tol &&
          Math.abs(r.width - last.width) < tol &&
          Math.abs(r.height - last.height) < tol
        ) {
          stable += 1;
          if (stable >= frames) {
            resolve(r);
            return;
          }
        } else {
          stable = 0;
          last = r;
        }
        requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    });
  }, []);

  // clamp picker so it doesn't fly off-screen
  const computeClampedPosition = useCallback((anchorRect) => {
    const pickerEl = pickerBoxRef.current;
    if (!pickerEl) return { top: 0, left: 0 };

    // measure picker
    const pw = pickerEl.offsetWidth || 340;
    const ph = pickerEl.offsetHeight || 320;

    const GAP = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // try below first
    let top = anchorRect.bottom + GAP;
    if (top + ph > vh - GAP) {
      // not enough space below → put it above
      top = Math.max(GAP, anchorRect.top - ph - GAP);
    }

    // try left-aligned with button
    let left = anchorRect.left;
    // clamp right edge
    if (left + pw > vw - GAP) {
      left = Math.max(GAP, vw - GAP - pw);
    }
    // clamp left edge
    if (left < GAP) {
      left = GAP;
    }

    return { top, left };
  }, []);

  // we call this after the picker is mounted, and whenever viewport changes while open
  const positionPickerStable = useCallback(
    async (revealAfter = false) => {
      const anchorEl = anchorRef.current;
      if (!anchorEl) return;

      // wait until anchor stops animating/sliding
      const stableRect = await waitForStableRect(anchorEl, 3, 0.5);

      // now that anchor is stable and pickerBoxRef exists,
      // calculate final clamped coords and commit them
      const finalPos = computeClampedPosition(stableRect);
      setCoords(finalPos);

      if (revealAfter) {
        // next frame: show it for real (prevents flash at (0,0))
        requestAnimationFrame(() => {
          setPickerVisible(true);
          justOpenedRef.current = false;
        });
      }
    },
    [computeClampedPosition, waitForStableRect]
  );

  // open picker: mount hidden, then `positionPickerStable(true)` handles reveal
  const openPicker = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    justOpenedRef.current = true;
    setPickerVisible(false); // start hidden
    setPickerOpen(true);
  }, []);

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setPickerVisible(false);
  }, []);

  // After pickerOpen flips true, run stable positioning once and reveal.
  useLayoutEffect(() => {
    if (!pickerOpen) return;
    // do the first precise placement after we render the portal'd picker
    // and only then flip visibility on.
    positionPickerStable(true);
  }, [pickerOpen, positionPickerStable]);

  // While picker is open:
  // if the viewport resizes or scrolls, re-clamp position (no need to hide/show again)
  useEffect(() => {
    if (!pickerOpen) return;

    const handleRelayout = () => {
      // we don't pass revealAfter because it's already visible,
      // we just want to recompute coords quickly
      positionPickerStable(false);
    };

    window.addEventListener("resize", handleRelayout);
    window.addEventListener("scroll", handleRelayout, true);

    return () => {
      window.removeEventListener("resize", handleRelayout);
      window.removeEventListener("scroll", handleRelayout, true);
    };
  }, [pickerOpen, positionPickerStable]);

  // Close on outside click/touch
  useEffect(() => {
    if (!pickerOpen) return;

    const onDown = (e) => {
      if (justOpenedRef.current) return;

      const insideAnchor = anchorRef.current?.contains(e.target);
      const insidePicker = pickerBoxRef.current?.contains(e.target);

      if (!insideAnchor && !insidePicker) {
        closePicker();
      }
    };

    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
    };
  }, [pickerOpen, closePicker]);

  return (
    <>
      {/* Smile trigger button (next to each message bubble) */}
      <button
        ref={anchorRef}
        onMouseDown={(e) => e.preventDefault()}
        onClick={openPicker}
        title={myRx ? "Change / remove your reaction" : "Add reaction"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 26,
          height: 26,
          borderRadius: 13,
          border: "1px dashed var(--border, #ddd)",
          background: "transparent",
          cursor: "pointer",
          opacity: 0.9,
        }}
      >
        <BsEmojiSmileUpsideDown size={16} />
      </button>

      {/* Picker Portal */}
      {pickerOpen &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 201,
            }}
          >
            <div
              ref={pickerBoxRef}
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                visibility: pickerVisible ? "visible" : "hidden",
                zIndex: 202,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <EmojiPicker
                onEmojiSelect={async (emojiObj) => {
                  await setMyReactionOnDoc(
                    docRef,
                    currentUser?.uid,
                    emojiObj.native
                  );
                  closePicker();
                }}
                onClickOutside={() => {
                  if (!justOpenedRef.current) closePicker();
                }}
              />

              {myRx && (
                <div style={{ marginTop: 6, textAlign: "right" }}>
                  <button
                    style={{
                      fontSize: 12,
                      opacity: 0.8,
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={async () => {
                      // toggle off my current emoji
                      await setMyReactionOnDoc(
                        docRef,
                        currentUser?.uid,
                        myRx.emoji
                      );
                      closePicker();
                    }}
                  >
                    Remove my reaction
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
